/** Настройки → ВКонтакте: чем публикуем, вход VK ID, ключ сообщества, запасной путь входа, поля сообщества. */
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, CircleAlert, CircleCheck, ClipboardPaste, LogIn, LogOut, RefreshCw, UserRound } from "lucide-react";
import type { PublicSettings, VkGroupTokenState, VkStatus } from "@impuls/shared";
import { api, errText } from "../lib/api";
import { useVkStatus } from "../lib/queries";
import { EcgLoader } from "./Ecg";
import { useConfirm, useToast } from "./ui";
import { SetBlock, SetField, SetKey, SetSaveBar, SetSection, setEnvHint, setHintId, useSetDirty, useSetDraft } from "./SettingsKit";

export type SetTone = "ok" | "warn" | "bad";

/** Итог подключения — для точки в навигации. Вход VK ID истёк, но ключ сообщества публикует — mode=group, warn. */
export function vkTone(st: VkStatus | undefined): SetTone | null {
  if (!st) return null;
  if (st.mode === "none" || !st.ok || !st.can_post) return "bad";
  return st.mode === "group" || !st.can_upload_photos ? "warn" : "ok";
}

/** Чем сейчас уходят посты — одной строкой. Права — из can_post / can_upload_photos, а не из mode. */
export function vkPublishing(st: VkStatus): { tone: SetTone; text: string } {
  if (st.mock) return { tone: "ok", text: "тестовый режим, во ВКонтакте ничего не уходит" };
  if (st.mode === "group") return { tone: st.ok ? "warn" : "bad", text: "через ключ сообщества (только текст)" };
  if (st.mode === "user") {
    // нет связи с ВК — права неизвестны, причина — строкой ниже
    if (!st.ok) return { tone: "bad", text: "через VK ID" };
    if (!st.can_post) return { tone: "bad", text: "не работает: у VK ID нет права «стена»" };
    if (!st.can_upload_photos) return { tone: "warn", text: "через VK ID (только текст)" };
    return { tone: "ok", text: "через VK ID (текст и фото)" };
  }
  return { tone: "bad", text: "не подключено" };
}

/** Права ключа сообщества из groups.getTokenPermissions — по-русски. */
const RIGHTS: Record<string, string> = {
  wall: "стена",
  photos: "фото",
  manage: "управление",
  stories: "истории",
  docs: "документы",
  messages: "сообщения",
  app_widget: "виджет",
  video: "видео",
  market: "товары",
  ads: "реклама",
};
export const vkRight = (p: string) => RIGHTS[p] ?? p;

export function SettingsVk({ s }: { s: PublicSettings }) {
  const vk = useVkStatus();
  const st = vk.data;
  return (
    <SetSection id="vk" title="ВКонтакте" status={(st?.mock ?? s.vk_mock) && <span className="chip chip--outline">Тестовый режим</span>}>
      <SetBlock>
        <VkTop st={st} checking={vk.isFetching} />
        {vk.isLoading ? (
          <div className="set-cards__loading">
            <EcgLoader label="Проверяю подключение" />
          </div>
        ) : (
          <>
            {vk.error && !st && (
              <div className="error-line" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>Не получилось узнать статус ВКонтакте: {errText(vk.error)}</span>
              </div>
            )}
            {st && st.mode !== "none" && (!st.ok || !st.can_post) && st.message && (
              <div className="error-line" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{st.message}</span>
              </div>
            )}
            <div className="set-cards">
              {st && <VkIdCard st={st} />}
              <VkGroupToken s={s} st={st} />
            </div>
          </>
        )}
        <VkPaste />
      </SetBlock>
      <VkFields s={s} st={st} />
    </SetSection>
  );
}

/* ---------- итог и проверка ---------- */
function VkTop({ st, checking: loading }: { st: VkStatus | undefined; checking: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const checking = busy || loading;
  const pub = st ? vkPublishing(st) : null;
  const check = async () => {
    setBusy(true);
    try {
      // fresh=1 — сервер сбрасывает кэш прав ключа и профиля VK ID; фоновые загрузки идут без него
      const r = await api.get<VkStatus>("/api/vk/check?fresh=1");
      qc.setQueryData(["vk"], r);
      if (vkPublishing(r).tone !== "bad") toast("ВКонтакте на связи");
      else toast(r.message ?? "ВКонтакте не подключён", "error");
    } catch (e) {
      toast(`Проверка не удалась: ${errText(e)}`, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="set-vk-top">
      {pub ? (
        <p className={`set-vk-sum set-vk-sum--${pub.tone}`} role="status">
          <span className="set-vk-sum__dot" aria-hidden="true" />
          Публикация: <strong>{pub.text}</strong>
        </p>
      ) : (
        <span />
      )}
      <button type="button" className="btn btn--small" onClick={() => void check()} disabled={checking}>
        {checking ? <EcgLoader size={26} /> : <RefreshCw size={16} aria-hidden="true" />}
        {checking ? "Проверяю…" : "Проверить подключение"}
      </button>
    </div>
  );
}

/* ---------- VK ID ---------- */
function VkIdCard({ st }: { st: VkStatus }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [leaving, setLeaving] = useState(false);
  const id = st.vkid;
  const name = id.user_name || (id.user_id ? `id${id.user_id}` : "аккаунт ВКонтакте");
  const tone = !id.logged_in ? "idle" : id.needs_relogin ? "bad" : id.can_wall ? "ok" : "warn";

  const logout = async () => {
    const ok = await confirm({
      title: "Выйти из VK ID?",
      text: "Запланированные посты останутся во ВКонтакте и выйдут сами.",
      confirm: "Выйти",
      danger: true,
    });
    if (!ok) return;
    setLeaving(true);
    try {
      const r = await api.post<VkStatus>("/api/vk/logout");
      qc.setQueryData(["vk"], r);
      void qc.invalidateQueries({ queryKey: ["stats"] });
      toast("Вы вышли из VK ID");
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div className={`set-card set-card--${tone}`} role="group" aria-labelledby="vk-id-title">
      <h3 id="vk-id-title" className="set-card__title">
        VK ID
      </h3>
      <div className="set-card__state">
        {id.logged_in && id.photo ? (
          <img className="set-card__avatar" src={id.photo} alt="" width={40} height={40} />
        ) : (
          <span className="set-card__icon" aria-hidden="true">
            {id.logged_in ? <CircleCheck size={20} strokeWidth={2} /> : <UserRound size={20} strokeWidth={2} />}
          </span>
        )}
        <div className="set-card__text">
          <p className="set-card__main">{id.logged_in ? `Вы вошли: ${name}` : "Не выполнен вход"}</p>
          {id.logged_in && id.needs_relogin && <p className="set-card__sub">Вход истёк — войдите заново</p>}
          {id.logged_in && !id.needs_relogin && !id.can_wall && (
            <p className="set-card__sub">ВК пока не дал право публиковать — ждём одобрения заявки</p>
          )}
        </div>
      </div>
      <div className="set-card__actions">
        <a className={`btn btn--small ${!id.logged_in || id.needs_relogin ? "btn--primary" : ""}`} href="/api/vk/login">
          <LogIn size={16} aria-hidden="true" />
          {id.logged_in ? "Войти заново" : "Войти через VK ID"}
        </a>
        {id.logged_in && (
          <button type="button" className="btn btn--small btn--ghost btn--danger" onClick={() => void logout()} disabled={leaving}>
            <LogOut size={16} aria-hidden="true" />
            {leaving ? "Выхожу…" : "Выйти"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- ключ сообщества ---------- */
function VkGroupToken({ s, st }: { s: PublicSettings; st: VkStatus | undefined }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  // пока статус ВК не пришёл — то, что знают настройки
  const gt: VkGroupTokenState = st?.group_token ?? {
    set: s.vk_group_token_set,
    mask: s.vk_group_token,
    locked: s.env_locked.includes("vk_group_token"),
    permissions: null,
    error: null,
  };
  const groupId = (s.vk_group_id || "").replace(/\D/g, "");

  const put = async (value: string) => {
    const r = await api.put<VkStatus>("/api/vk/group-token", { value });
    qc.setQueryData(["vk"], r);
    qc.setQueryData<PublicSettings>(["settings"], (old) =>
      old ? { ...old, vk_group_token_set: r.group_token.set, vk_group_token: r.group_token.mask } : old,
    );
    void qc.invalidateQueries({ queryKey: ["settings"] });
    void qc.invalidateQueries({ queryKey: ["stats"] });
  };

  return (
    <SetKey
      id="vk-group-token"
      title="Ключ сообщества"
      isSet={gt.set}
      mask={gt.mask}
      locked={gt.locked}
      envKey="vk_group_token"
      placeholder="vk1.a.…"
      validate={(v) => (v.length < 20 ? "Не похоже на ключ сообщества — скопируйте его целиком." : "")}
      onSave={async (v) => {
        await put(v);
        toast("Ключ сообщества сохранён");
      }}
      onRemove={async () => {
        const ok = await confirm({
          title: "Удалить ключ сообщества?",
          text: "Вход VK ID это не затронет.",
          confirm: "Удалить ключ",
          danger: true,
        });
        if (!ok) return false;
        await put("");
        toast("Ключ сообщества удалён");
        return true;
      }}
      details={<VkTokenRights gt={gt} />}
      help={
        groupId && (
          <a href={`https://vk.com/club${groupId}?act=tokens`} target="_blank" rel="noreferrer">
            Где взять ключ
          </a>
        )
      }
    />
  );
}

function VkTokenRights({ gt }: { gt: VkGroupTokenState }) {
  if (!gt.set) return null;
  if (gt.permissions) {
    return (
      <>
        <p className="set-card__sub">Права: {gt.permissions.length ? gt.permissions.map(vkRight).join(", ") : "нет"}</p>
        {!gt.permissions.includes("wall") && <p className="set-card__sub set-card__sub--warn">Нет доступа к стене — посты не уйдут</p>}
      </>
    );
  }
  if (gt.error) return <p className="set-card__sub set-card__sub--warn">Права не проверены: {gt.error}</p>;
  return null;
}

/* ---------- запасной путь входа ---------- */
function VkPaste() {
  const toast = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post<VkStatus>("/api/vk/callback-paste", { url: url.trim() });
      qc.setQueryData(["vk"], r);
      void qc.invalidateQueries({ queryKey: ["stats"] });
      toast("Вы вошли через VK ID");
      setUrl("");
      setOpen(false);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="set-details set-details--compact" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        <ClipboardPaste size={16} aria-hidden="true" />
        Вставить адрес после входа
        <ChevronDown className="set-details__chev" size={16} aria-hidden="true" />
      </summary>
      <form className="set-details__body" onSubmit={submit} noValidate>
        <SetField id="vk-paste" label="Адрес страницы kemin22.github.io после входа">
          <div className="set-inline-form">
            <input
              id="vk-paste"
              className="input"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError("");
              }}
              placeholder="https://kemin22.github.io/vk-callback/?code=…"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
            />
            <button className="btn btn--dark" disabled={busy || !url.trim()}>
              {busy ? "Подключаю…" : "Подключить"}
            </button>
          </div>
        </SetField>
        {error && (
          <div className="error-line" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
      </form>
    </details>
  );
}

/* ---------- сообщество и приложение ---------- */
function VkFields({ s, st }: { s: PublicSettings; st: VkStatus | undefined }) {
  const toast = useToast();
  const qc = useQueryClient();
  const locked = new Set(s.env_locked);
  const d = useSetDraft({ vk_group_id: s.vk_group_id, vk_group_name: s.vk_group_name, vk_client_id: s.vk_client_id });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useSetDirty("vk-fields", d.dirty);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = d.value;
    if (!/^\d{1,12}$/.test(v.vk_group_id.trim())) return setError("ID сообщества — это число, например 200184597.");
    if (!/^\d{1,12}$/.test(v.vk_client_id.trim())) return setError("ID приложения — это число, например 54840568.");
    if (!v.vk_group_name.trim()) return setError("Укажите название сообщества.");
    const body: Record<string, string> = {};
    for (const [k, val] of Object.entries(d.changes)) if (!locked.has(k)) body[k] = String(val).trim();
    setBusy(true);
    setError("");
    try {
      const r = await api.put<PublicSettings>("/api/settings", body);
      qc.setQueryData(["settings"], r);
      d.reset();
      void qc.invalidateQueries({ queryKey: ["vk"] });
      toast("Настройки ВКонтакте сохранены");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SetBlock
      title="Сообщество и приложение"
      aside={
        st?.group && (
          <a className="set-block__link" href={`https://vk.com/club${st.group.id}`} target="_blank" rel="noreferrer">
            {st.group.name}
          </a>
        )
      }
    >
      <form className="set-form" onSubmit={submit} noValidate>
        <div className="set-grid">
          <SetField id="vk-group-id" label="ID сообщества" hint={locked.has("vk_group_id") && setEnvHint("vk_group_id")}>
            <input
              id="vk-group-id"
              className="input"
              inputMode="numeric"
              value={d.value.vk_group_id}
              onChange={(e) => d.set("vk_group_id", e.target.value)}
              readOnly={locked.has("vk_group_id")}
              autoComplete="off"
              aria-describedby={locked.has("vk_group_id") ? setHintId("vk-group-id") : undefined}
            />
          </SetField>
          <SetField id="vk-group-name" label="Название для превью">
            <input
              id="vk-group-name"
              className="input"
              value={d.value.vk_group_name}
              onChange={(e) => d.set("vk_group_name", e.target.value)}
              maxLength={120}
              autoComplete="off"
            />
          </SetField>
          <SetField id="vk-client-id" label="ID приложения VK ID" hint={locked.has("vk_client_id") && setEnvHint("vk_client_id")}>
            <input
              id="vk-client-id"
              className="input"
              inputMode="numeric"
              value={d.value.vk_client_id}
              onChange={(e) => d.set("vk_client_id", e.target.value)}
              readOnly={locked.has("vk_client_id")}
              autoComplete="off"
              aria-describedby={locked.has("vk_client_id") ? setHintId("vk-client-id") : undefined}
            />
          </SetField>
        </div>
        <SetSaveBar
          dirty={d.dirty}
          busy={busy}
          error={error}
          onReset={() => {
            d.reset();
            setError("");
          }}
        />
      </form>
    </SetBlock>
  );
}
